import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/Common/PageHeader';
import ModernPageHeader, { ModernStatCard, ModernActionButton } from '../components/Common/ModernPageHeader';
import ModernCard from '../components/Common/ModernCard';
import '../styles/modern-enhancements.css';
import { useUser } from '../contexts/UserContext';
import { useBrand } from '../contexts/BrandContext';
import { getDashboardArt } from '../utils/dashboardArt';
import { useAdminDashboardData } from '../hooks/useAdminDashboardData';
import { Avatar } from '../utils/avatarUtils';
import InventoryAlertsWidget from '../components/Dashboard/InventoryAlertsWidget';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { fmtDate } from '../utils/dateFormatters';
import FinishSetupBanner from '../components/Dashboard/FinishSetupBanner';
import {
  UsersIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  PlusIcon,
  ClockIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  CalendarIcon,
  CogIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

// Art is resolved per brand at render (see utils/dashboardArt.js) rather than being a
// module constant: three of these six illustrations are trade-specific, so a shared set
// showed an SMBMate accountant a toolbox and a wrench.

// Helper functions at module level
const calcDelta = (current, previous) => {
  if (!previous || previous === 0 || current === undefined || current === null) return null;
  return Math.round(((current - previous) / previous) * 100);
};

// Helper to get previous period dates based on preset
const getPrevPeriod = (preset, start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  
  switch (preset) {
    case 'TODAY':
      s.setDate(s.getDate() - 1);
      e.setDate(e.getDate() - 1);
      break;
    case 'WEEK':
      s.setDate(s.getDate() - 7);
      e.setDate(e.getDate() - 7);
      break;
    case 'MONTH':
      s.setMonth(s.getMonth() - 1);
      e.setMonth(e.getMonth() - 1);
      break;
    case 'LAST_30':
      s.setDate(s.getDate() - 30);
      e.setDate(e.getDate() - 30);
      break;
    default:
      s.setMonth(s.getMonth() - 1);
      e.setMonth(e.getMonth() - 1);
  }
  
  return { start: s, end: e };
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const queryClient = useQueryClient();

  // brand.code is the company's brand once logged in (BrandContext resolves it from
  // companies.brand), so the dashboard follows the account, not the domain.
  const { brand, t } = useBrand();
  const DASHBOARD_ART = getDashboardArt(brand?.code);

  // The saved range is read here, before the first query, not in an effect after it: the
  // effect fetched Last 30 Days first and then the saved range, two dashboard loads per visit.
  const [range, setRange] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboard_range');
      if (saved) return JSON.parse(saved);
    } catch {}
    return { preset: 'LAST_30', start: null, end: null };
  });

  // Recent activity stays collapsed so live operations retain priority.
  const [recentActivityExpanded, setRecentActivityExpanded] = useState(false);

  // Helper to get friendly date range label. KPI subtitles MUST use this rather
  // than a literal: the values above them follow the Today/Week/Month/Last-30
  // selector, so a hardcoded 'This month' actively lies about what is on screen
  // (F-IRIS-0815-1530-S).
  // Helper to get friendly date range label
  const getRangeLabel = () => {
    if (range.preset === 'TODAY') return 'Today';
    if (range.preset === 'WEEK') return 'This Week';
    if (range.preset === 'MONTH') return 'This Month';
    if (range.preset === 'LAST_30') return 'Last 30 Days';
    if (range.start && range.end) {
      const s = fmtDate(range.start, { month: 'short', day: 'numeric' });
      const e = fmtDate(range.end, { month: 'short', day: 'numeric' });
      return `${s} - ${e}`;
    }
    return 'Last 30 Days';
  };

  // React Query hooks for data fetching
  const { data: dashboardDataRaw, isLoading: dashboardLoading } = useAdminDashboardData(user?.company_id, range);
  const dashboardData = dashboardDataRaw || {};
  // Recent Activity and Action Required come from the same single summary read (page speed
  // phase 1); they were two more query sets, and Risks ran twice. As separate queries they stayed
  // on screen while a new range loaded, so the last loaded lists are kept until the next arrive.
  const lastSummaryRef = useRef(null);
  if (dashboardDataRaw) lastSummaryRef.current = dashboardDataRaw;
  const recentActivity = lastSummaryRef.current?.recentActivity || [];
  const risks = lastSummaryRef.current?.risks || [];

  // Set loading state based on query state
  const loading = dashboardLoading || !dashboardDataRaw;

  // Guard all dashboard data properties with defaults
  const safeData = {
    // Numeric defaults
    activeEmployees: dashboardData?.activeEmployees ?? 0,
    // "Jobs This Range" and the "Active Jobs" card used to read the SAME field, so one
    // number appeared under two headings making incompatible promises. They are now the
    // two counts they were always described as.
    jobsThisWeek: dashboardData?.jobsThisWeek ?? 0,
    activeJobs: dashboardData?.activeJobs ?? 0,
    monthlyRevenue: dashboardData?.monthlyRevenue ?? 0,
    completionRate: dashboardData?.completionRate ?? 0,
    unscheduledJobs: dashboardData?.unscheduledJobs ?? 0,
    quoteConversionRate: dashboardData?.quoteConversionRate ?? 0,
    sentQuotes: dashboardData?.sentQuotes ?? 0,
    acceptedQuotes: dashboardData?.acceptedQuotes ?? 0,
    overdueJobs: dashboardData?.overdueJobs ?? 0,
    utilizationPct: dashboardData?.utilizationPct ?? 0,
    scheduledHours: dashboardData?.scheduledHours ?? 0,
    capacityHours: dashboardData?.capacityHours ?? 0,
    outstandingInvoices: dashboardData?.outstandingInvoices ?? 0,
    outstandingInvoiceAmount: dashboardData?.outstandingInvoiceAmount ?? 0,
    bookedRevenue: dashboardData?.bookedRevenue ?? 0,
    uninvoicedCompleted: dashboardData?.uninvoicedCompleted ?? 0,
    missedRevenue: dashboardData?.missedRevenue ?? 0,
    // Operations scorecard
    onTimePct: dashboardData?.onTimePct ?? 0,
    backlogAging: dashboardData?.backlogAging ?? 0,
    cancellationRate: dashboardData?.cancellationRate ?? 0,
    noShowCount: dashboardData?.noShowCount ?? 0,
    avgJobDuration: dashboardData?.avgJobDuration ?? 0,
    quoteToJobConversion: dashboardData?.quoteToJobConversion ?? 0,
    // Operations scorecard sample sizes (0 => show "—" instead of a misleading 0%/0h)
    onTimeSampleCount: dashboardData?.onTimeSampleCount ?? 0,
    backlogCount: dashboardData?.backlogCount ?? 0,
    totalJobsCount: dashboardData?.totalJobsCount ?? 0,
    durationSampleCount: dashboardData?.durationSampleCount ?? 0,
    sentQuotesCount: dashboardData?.sentQuotesCount ?? 0,
    // Array defaults
    revenueSeries: dashboardData?.revenueSeries || [],
    techScoreCards: dashboardData?.techScoreCards || [],
    capacityHeatmap: dashboardData?.capacityHeatmap || [],
    unassignedJobs: dashboardData?.unassignedJobs || [],
    unassignedCount: dashboardData?.unassignedCount ?? (dashboardData?.unassignedJobs?.length || 0),
    arAging: dashboardData?.arAging || { bucket0_30: 0, bucket31_60: 0, bucket61_90: 0, bucket90_plus: 0 },
    // #3911: genuinely past-due receivable, computed from the same invoices as the buckets.
    overdueTotal: dashboardData?.overdueTotal ?? 0,
    overdueCount: dashboardData?.overdueCount ?? 0
  };

  useEffect(() => {
    try { localStorage.setItem('dashboard_range', JSON.stringify(range)); } catch {}
  }, [range]);

  // 🔒 LOGIN ENFORCEMENT - Must be after all hooks
  if (!user) {
    navigate('/login');
    return null;
  }

  // 🔒 ROLE GUARDS - Only allow admin dashboard access to these roles
  const allowedRoles = ['APP_OWNER', 'OWNER', 'ADMIN'];
  if (!allowedRoles.includes(user.role)) {
    navigate('/unauthorized');
    return null;
  }

  // Helper components
  const severityOrder = { high: 0, medium: 1, low: 2 };

  // Main KPI Stats
  const kpiStats = [
    {
      title: 'Active Employees',
      value: loading ? '-' : safeData.activeEmployees.toString(),
      icon: UsersIcon,
      onClick: () => navigate('/employees')
    },
    {
      title: 'Jobs This Range',
      value: loading ? '-' : safeData.jobsThisWeek.toString(),
      icon: CalendarDaysIcon,
      onClick: () => navigate('/jobs')
    },
    {
      title: 'Cash Revenue',
      value: loading ? '-' : `$${Number(safeData.monthlyRevenue).toLocaleString()}`,
      icon: CurrencyDollarIcon,
      onClick: () => navigate('/invoices'),
      extra: !loading && (
        <div className="mt-2">
          <ResponsiveContainer width="100%" height={54}>
            <AreaChart data={safeData.revenueSeries.map((v,i)=>({ i, v }))}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9CA3AF" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#9CA3AF" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="i" hide />
              <YAxis hide />
              <RTooltip formatter={(v)=>[`$${Number(v).toLocaleString()}`, 'Revenue']} cursor={{ stroke: '#E5E7EB' }} />
              <Area type="monotone" dataKey="v" stroke="#9CA3AF" fillOpacity={1} fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )
    },
    {
      title: 'Completion Rate',
      value: loading ? '-' : `${safeData.completionRate}%`,
      icon: ChartBarIcon,
      onClick: () => navigate('/jobs?filter=completed')
    }
  ];

  // Helper components
  const AgingPill = ({ label, value }) => (
    <div className="dashboard-data-pill flex items-center justify-between px-3 py-2 rounded-md border">
      <span className="text-xs">{label}</span>
      <span className="text-sm font-medium">{shownMoney(value)}</span>
    </div>
  );

  const ProgressBar = ({ pct }) => (
    <div className="dashboard-progress w-full h-2 rounded">
      <div className="dashboard-progress__value h-2 rounded" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  );

  const fmtMoney = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));
  // Board #5661. Panels below the KPI cards rendered $0 / 0% for the seconds before data
  // arrived, which reads as a real figure ("$0 outstanding"). Show '-' until it loads, as the
  // KPI cards already do.
  const shownMoney = (n) => (loading ? '-' : fmtMoney(n));

  const ar = safeData.arAging;
  const arTotal = (Number(ar.bucket0_30)||0) + (Number(ar.bucket31_60)||0) + (Number(ar.bucket61_90)||0) + (Number(ar.bucket90_plus)||0);

  // Operations Scorecard: a metric with a zero denominator has no data yet — show "—"
  // (neutral styling) rather than a 0% / 0h that reads like a measured-poor result.
  const opsHasOnTime = safeData.onTimeSampleCount > 0;
  const opsHasBacklog = safeData.backlogCount > 0;
  const opsHasCancel = safeData.totalJobsCount > 0;
  const opsHasDuration = safeData.durationSampleCount > 0;
  const opsHasQuoteConv = safeData.sentQuotesCount > 0;
  const opsNeutralBorder = 'border-gray-200/60 dark:border-gray-600/30 bg-gray-100/20 dark:bg-gray-800/20';
  const opsNeutralText = 'text-gray-400 dark:text-gray-500';

  return (
    <div className="business-dashboard business-dashboard--admin relative fade-in space-y-8">
      {/* The way back into setup. Onboarding could be skipped or left half-done and
          nothing in the app linked to /onboarding, so "finish it later" had nowhere to
          happen. Renders only while setup is genuinely unfinished. */}
      <FinishSetupBanner />

      {/* Modern Page Header */}
      <ModernPageHeader
        // Renamed 2026-08-28. Two problems, one rename:
        //  1. The DashboardRouter toggle that opens this page calls it "Admin Dashboard"
        //     while the page called itself "Executive Dashboard" — one page, two names.
        //  2. "Executive" also named a TAB inside Reports, so the two collided.
        // "Business Dashboard" pairs with "My Dashboard" (personal vs business, which is
        // exactly what the toggle switches between) and avoids "Admin", which CLAUDE.md §6
        // reserves for the Platform Console — this page is the business OWNER's dashboard,
        // not an internal admin surface.
        title="Business Dashboard"
        subtitle="A clear view of the work, people, and cash moving through your business."
        icon={ChartBarIcon}
        // COMPACT, 2026-09-01 (operator ruling). This was the last page on the gradient hero
        // and the only one in the app using artSrc — the illustrated masthead with its grid
        // and blueprint background. That is exactly why it read as a different product: it
        // sits behind the My Dashboard / Business Dashboard toggle, whose other half is now
        // a plain title bar, so one control switched between two design languages.
        //
        // variant="business-dashboard" and the art props are dropped rather than kept: the
        // bespoke render branch and its CSS are now unused, and are LEFT IN PLACE per §20
        // (orphaned is not dead) in case an illustrated dashboard is wanted deliberately
        // later. Restoring it is putting these three props back.
        compact
        eyebrow="BUSINESS OVERVIEW"
        actions={[
          {
            label: 'View Reports',
            icon: DocumentTextIcon,
            // #4838: open Reports on the period this dashboard is showing, as the KPI tiles
            // already do (#5556) — the bare link landed on Reports' own This Month default.
            onClick: () => navigate(dashboardData?.rangeStartDay && dashboardData?.rangeEndDay
              ? `/reports?start=${dashboardData.rangeStartDay}&end=${dashboardData.rangeEndDay}`
              : '/reports')
          }
        ]}
      />

      {/* Risk Inbox - Action Required */}
      {risks.length > 0 && (
        <ModernCard className="dashboard-panel">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                Action Required ({risks.length})
              </h3>
            </div>
            <div className="space-y-3">
              {risks.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]).map(risk => {
                // Determine action button based on risk type
                let actionLabel = risk.action.label;
                let actionPath = risk.action.path;
                if (risk.type === 'overdue_invoices') {
                  actionLabel = 'Send Reminder';
                  actionPath = '/invoices?filter=overdue';
                } else if (risk.type === 'aging_quotes') {
                  actionLabel = 'Follow Up';
                  // `pending` was not a filter the Quotes page has ever read, so this
                  // opened the default all-quotes view: a count of SENT quotes answered
                  // with a list including drafts. `aging` is the same predicate the card
                  // counts — sent, waiting more than seven days.
                  actionPath = '/quotes?filter=aging';
                } else if (risk.type === 'unscheduled_high_value') {
                  actionLabel = 'Schedule Now';
                  actionPath = '/calendar?techFilter=all';
                } else if (risk.type === 'today_no_tech') {
                  actionLabel = 'Assign Tech';
                  actionPath = '/calendar?view=resourceTimeGridDay&techFilter=all';
                } else if (risk.type === 'overdue_jobs') {
                  actionLabel = 'View Job';
                  actionPath = '/jobs';
                }
                // These rows were switched to dark-only tints because the panel they sit in
                // was dark unconditionally — the note here said so, and said re-theming the
                // panel was "a much larger change than one contrast finding warrants". The
                // operator asked for light mode to work on 2026-09-26, so the panel IS theme-
                // aware now (modern-enhancements.css scopes its dark values under .dark), and
                // these tints go back to a light/dark pair. Both halves matter: the titles
                // carry no colour class of their own, so they inherit the panel's text colour,
                // and a tint that does not follow the theme puts near-white on bg-yellow-50 at
                // about 1.07:1 against a 4.5:1 AA bar.
                return (
                  // Board #1596: the row carried cursor-pointer and a hover shadow but no
                  // handler, so the whole Action Required item advertised itself as clickable
                  // and did nothing. The labelled CTA on the right is the action — it names
                  // what it does ("Send Reminder", "Assign Tech"), which a whole-row click
                  // cannot. Affordance removed rather than duplicated onto the row.
                  <div key={risk.id} className={`flex items-center justify-between p-3 rounded-lg ${
                    risk.severity === 'high' ? 'bg-red-50 dark:bg-red-900/25 border-l-4 border-red-500' :
                    risk.severity === 'medium' ? 'bg-yellow-50 dark:bg-yellow-900/25 border-l-4 border-yellow-500' :
                    'bg-blue-50 dark:bg-blue-900/25 border-l-4 border-blue-500'
                  }`}>
                    <div>
                      <span className="font-medium text-sm">{risk.title}</span>
                      <p className="text-xs text-gray-500">{risk.description}</p>
                    </div>
                    <button onClick={() => navigate(actionPath)}
                      className="dashboard-link text-xs hover:underline whitespace-nowrap">
                      {actionLabel} →
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </ModernCard>
      )}

      {/* Live Operations */}
      <ModernCard className="dashboard-panel">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" /> Dispatch Control
          </h3>

          {/* 7-Day Capacity Heatmap */}
          <div className="flex gap-2 mb-4">
            {safeData.capacityHeatmap.map((day) => (
              <div key={day.date} className={`flex-1 text-center p-2 rounded-lg ${day.colorClass}`}>
                <div className="text-xs font-medium">{day.dayName}</div>
                <div className="text-lg font-bold">{day.jobCount}</div>
                <div className="text-[10px]">{day.fillRate}% full</div>
              </div>
            ))}
          </div>

          {/* Unassigned Jobs */}
          {safeData.unassignedJobs.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Needs Assignment ({safeData.unassignedCount > safeData.unassignedJobs.length ? `${safeData.unassignedJobs.length} of ${safeData.unassignedCount}` : safeData.unassignedCount})</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {safeData.unassignedJobs.map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/25 rounded border-l-2 border-yellow-400">
                    <div>
                      <span className="text-sm font-medium">{job.title}</span>
                      <span className="text-xs text-gray-500 ml-2">{job.customerName}</span>
                      <span className="text-xs text-gray-400 ml-2">{fmtMoney(job.total_amount)}</span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => navigate(`/jobs?edit=${job.id}`)} className="text-xs text-gray-600 hover:text-gray-800">View</button>
                      <button onClick={() => navigate('/calendar?view=resourceTimeGridDay&techFilter=all')} className="dashboard-link text-xs">Assign →</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {safeData.unassignedJobs.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-2">
              {(safeData.capacityHeatmap || []).some(d => (d.jobCount || 0) > 0)
                ? 'All jobs assigned ✓'
                : 'No jobs scheduled this week'}
            </div>
          )}
        </div>
      </ModernCard>

      {/* Range-based reporting begins here. */}
      <div className="dashboard-control-deck dashboard-control-deck--range">
        <div>
          <span className="dashboard-control-label">Reporting period</span>
          {/* Says which cards actually move. "Performance metrics below update to match this
              range" promised all four, and two of them are current-state BY DESIGN: Active
              Jobs is what is on the books right now and Outstanding A/R is what is owed right
              now — neither means anything scoped to last week. The behaviour was right and
              the sentence above it was not, so toggling the range and watching two numbers
              sit still read as a broken filter. */}
          <p>Cash Collected and Team Utilization follow this range. Active Jobs and Outstanding A/R show the position right now.</p>
        </div>
        <div className="dashboard-range-controls">
          <div className="segmented-modern" aria-label="Reporting period">
            {['TODAY','WEEK','MONTH','LAST_30'].map(p => (
              <button
                key={p}
                onClick={() => setRange({ preset: p, start: null, end: null })}
                className={`segmented-btn ${range.preset === p ? 'active' : ''}`}
              >
                {p === 'LAST_30' ? 'Last 30 Days' : p.charAt(0) + p.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Business Snapshot */}
      <div>
        <div className="dashboard-section-heading">
          <div>
            <span>Business pulse</span>
            <h2>Business snapshot</h2>
          </div>
          <p>{getRangeLabel()}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <ModernStatCard
            title="Cash Collected"
            value={fmtMoney(safeData.monthlyRevenue)}
            // Board #971 (6 reports): the window was only in the section heading, so this
            // read as a different number from Reports' Cash Collected (month to date by
            // default). State it on the tile itself.
            change={`Payments received · ${getRangeLabel()}`}
            changeType="neutral"
            icon={CurrencyDollarIcon}
            gradient="blue"
            artSrc={DASHBOARD_ART.revenue.src}
            artFallbackSrc={DASHBOARD_ART.revenue.fallbackSrc}
            loading={loading}
            onClick={() => navigate(dashboardData?.rangeStartDay && dashboardData?.rangeEndDay
              ? `/reports?start=${dashboardData.rangeStartDay}&end=${dashboardData.rangeEndDay}`
              : '/reports')}
            variant="business-dashboard"
          />
          <ModernStatCard
            title="Active Jobs"
            value={safeData.activeJobs}
            icon={CalendarDaysIcon}
            gradient="green"
            artSrc={DASHBOARD_ART.jobs.src}
            artFallbackSrc={DASHBOARD_ART.jobs.fallbackSrc}
            loading={loading}
            onClick={() => navigate('/jobs?filter=active')}
            variant="business-dashboard"
          />
          <ModernStatCard
            title="Team Utilization"
            value={`${safeData.utilizationPct}%`}
            icon={UserGroupIcon}
            gradient="orange"
            artSrc={DASHBOARD_ART.completion.src}
            artFallbackSrc={DASHBOARD_ART.completion.fallbackSrc}
            loading={loading}
            onClick={() => navigate('/calendar?techFilter=all')}
            variant="business-dashboard"
          />
          <ModernStatCard
            title="Outstanding A/R"
            value={fmtMoney(arTotal)}
            icon={BanknotesIcon}
            gradient="indigo"
            artSrc={DASHBOARD_ART.receivables.src}
            artFallbackSrc={DASHBOARD_ART.receivables.fallbackSrc}
            loading={loading}
            onClick={() => navigate('/invoices?filter=unpaid')}
            variant="business-dashboard"
          />
        </div>
      </div>

      {/* Revenue Pipeline */}
      <ModernCard className="dashboard-panel dashboard-pipeline">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Revenue Pipeline</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div 
                // Payments received in the period, as the top Cash Collected card links: every paid
                // invoice ever (34, $49,416.85) was a different population (audit 2026-10-17).
                onClick={() => navigate(dashboardData?.rangeStartDay && dashboardData?.rangeEndDay
                  ? `/reports?start=${dashboardData.rangeStartDay}&end=${dashboardData.rangeEndDay}`
                  : '/reports')}
                className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="text-2xl font-bold text-green-600">{shownMoney(safeData.monthlyRevenue)}</div>
                <div className="text-xs text-gray-500">Cash Collected</div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500">received {getRangeLabel()}</div>
              </div>
              <div 
                // #5821: a company-wide figure, so the calendar opens on everyone rather than
                // the technician filter this user last left the calendar on.
                onClick={() => navigate('/calendar?techFilter=all')}
                className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{shownMoney(safeData.bookedRevenue)}</div>
                <div className="text-xs text-gray-500">Booked (4 wks)</div>
              </div>
              <div 
                onClick={() => navigate('/invoices?filter=unpaid')}
                className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-600">{shownMoney(safeData.outstandingInvoiceAmount)}</div>
                <div className="text-xs text-gray-500">Outstanding</div>
              </div>
              <div 
                onClick={() => navigate('/jobs?filter=completed')}
                className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="text-2xl font-bold text-orange-600">{shownMoney(safeData.uninvoicedCompleted)}</div>
                <div className="text-xs text-gray-500">Uninvoiced</div>
              </div>
              <div 
                // #5819: the cancelled list, on the same window this figure covers.
                onClick={() => navigate(dashboardData?.rangeStartDay && dashboardData?.rangeEndDay
                  ? `/jobs/history?status=cancelled&start=${dashboardData.rangeStartDay}&end=${dashboardData.rangeEndDay}`
                  : '/jobs/history?status=cancelled')}
                className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{shownMoney(safeData.missedRevenue)}</div>
                {/* Board #1348. This read "Cancelled" beside a "Cancel Rate" of 0% and the pair
                    looked self-contradictory. They are not: this is money on jobs cancelled IN
                    this period — mostly jobs created before it — while the rate asks what share
                    of jobs CREATED in this period got cancelled. Both are true at once. Neither
                    said so, which is what made them read as a contradiction, so each now states
                    its own scope. */}
                <div className="text-xs text-gray-500">Cancelled</div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500">cancelled {getRangeLabel()}</div>
              </div>
            </div>
          </div>
        </ModernCard>

      {/* Operations Scorecard */}
      <div className="mb-8">
        <div className="dashboard-panel rounded-3xl backdrop-blur-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Operations Scorecard</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">Performance metrics</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* On-Time % */}
              <div className={`p-4 rounded-2xl border backdrop-blur-sm ${
                !opsHasOnTime ? opsNeutralBorder :
                safeData.onTimePct >= 85 ? 'border-green-300/50 bg-green-100/30 dark:bg-green-900/20' :
                safeData.onTimePct >= 70 ? 'border-yellow-300/50 bg-yellow-100/30 dark:bg-yellow-900/20' :
                'border-red-300/50 bg-red-100/30 dark:bg-red-900/20'
              }`}>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">On-Time %</div>
                <div className={`text-2xl font-bold ${
                  !opsHasOnTime ? opsNeutralText :
                  safeData.onTimePct >= 85 ? 'text-green-700 dark:text-green-400' :
                  safeData.onTimePct >= 70 ? 'text-yellow-700 dark:text-yellow-400' :
                  'text-red-700 dark:text-red-400'
                }`}>
                  {loading ? '-' : (opsHasOnTime ? `${safeData.onTimePct}%` : '—')}
                </div>
              </div>

              {/* Backlog Aging */}
              <div className={`p-4 rounded-2xl border backdrop-blur-sm ${
                !opsHasBacklog ? opsNeutralBorder :
                safeData.backlogAging <= 3 ? 'border-green-300/50 bg-green-100/30 dark:bg-green-900/20' :
                safeData.backlogAging <= 7 ? 'border-yellow-300/50 bg-yellow-100/30 dark:bg-yellow-900/20' :
                'border-red-300/50 bg-red-100/30 dark:bg-red-900/20'
              }`}>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Backlog Age</div>
                <div className={`text-2xl font-bold ${
                  !opsHasBacklog ? opsNeutralText :
                  safeData.backlogAging <= 3 ? 'text-green-700 dark:text-green-400' :
                  safeData.backlogAging <= 7 ? 'text-yellow-700 dark:text-yellow-400' :
                  'text-red-700 dark:text-red-400'
                }`}>
                  {loading ? '-' : (opsHasBacklog ? `${safeData.backlogAging} days` : '—')}
                </div>
              </div>

              {/* Cancellation Rate */}
              <div className={`p-4 rounded-2xl border backdrop-blur-sm ${
                !opsHasCancel ? opsNeutralBorder :
                safeData.cancellationRate <= 5 ? 'border-green-300/50 bg-green-100/30 dark:bg-green-900/20' :
                safeData.cancellationRate <= 10 ? 'border-yellow-300/50 bg-yellow-100/30 dark:bg-yellow-900/20' :
                'border-red-300/50 bg-red-100/30 dark:bg-red-900/20'
              }`}>
                {/* Board #1348: a COHORT rate — of the jobs created in this period, how many are
                    now cancelled. Stated on the tile because read beside the Cancelled money
                    figure, which counts cancellations that HAPPENED in the period, 0% next to a
                    non-zero amount looks like a bug and is not one. */}
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1" title="Share of jobs CREATED in this period that are now cancelled. The Cancelled money tile counts cancellations that happened in this period, including jobs created earlier — so these two can differ.">Cancel Rate</div>
                <div className={`text-2xl font-bold ${
                  !opsHasCancel ? opsNeutralText :
                  safeData.cancellationRate <= 5 ? 'text-green-700 dark:text-green-400' :
                  safeData.cancellationRate <= 10 ? 'text-yellow-700 dark:text-yellow-400' :
                  'text-red-700 dark:text-red-400'
                }`}>
                  {loading ? '-' : (opsHasCancel ? `${safeData.cancellationRate}%` : '—')}
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">of jobs created {getRangeLabel()}</div>
              </div>

              {/* 'No-Shows' REMOVED 2026-08-29 — it is not a no-show count and there is
                  no no-show concept in the schema. Nothing anywhere records a customer
                  not being present. The query behind it was
                  `status = 'scheduled' AND scheduled_start < 2 hours ago`, which is the
                  same idea as Overdue Jobs (`scheduled_end < today AND status IN
                  (scheduled, in_progress)`) — a job whose time has passed and which is
                  still marked scheduled. Both returned 41 on 48f32d34, which is a large
                  part of why this page shows 41 in several places and reads as broken.
                  A late job is not a no-show, and keeping both would have preserved the
                  duplication under two names in two different sections. */}

              {/* Average Job Duration */}
              <div className={`p-4 rounded-2xl border backdrop-blur-sm ${
                opsHasDuration ? 'border-blue-300/50 bg-blue-100/30 dark:bg-blue-900/20' : opsNeutralBorder
              }`}>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Avg Duration</div>
                <div className={`text-2xl font-bold ${opsHasDuration ? 'text-blue-700 dark:text-blue-400' : opsNeutralText}`}>
                  {loading ? '-' : (opsHasDuration ? `${safeData.avgJobDuration}h` : '—')}
                </div>
              </div>

              {/* Quote to Job Conversion */}
              <div className={`p-4 rounded-2xl border backdrop-blur-sm ${
                !opsHasQuoteConv ? opsNeutralBorder :
                safeData.quoteToJobConversion >= 50 ? 'border-green-300/50 bg-green-100/30 dark:bg-green-900/20' :
                safeData.quoteToJobConversion >= 30 ? 'border-yellow-300/50 bg-yellow-100/30 dark:bg-yellow-900/20' :
                'border-red-300/50 bg-red-100/30 dark:bg-red-900/20'
              }`}>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Quote→Job</div>
                <div className={`text-2xl font-bold ${
                  !opsHasQuoteConv ? opsNeutralText :
                  safeData.quoteToJobConversion >= 50 ? 'text-green-700 dark:text-green-400' :
                  safeData.quoteToJobConversion >= 30 ? 'text-yellow-700 dark:text-yellow-400' :
                  'text-red-700 dark:text-red-400'
                }`}>
                  {loading ? '-' : (opsHasQuoteConv ? `${safeData.quoteToJobConversion}%` : '—')}
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Operational Health */}
      <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* A/R Aging */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">A/R Aging</h3>
            <div className="text-sm text-gray-500">Total: {shownMoney(arTotal)}</div>
          </div>
          
          {/* Overdue Invoices Quick View */}
          {(() => {
            // Board #3911. This summed bucket61_90 + bucket90_plus, so everything overdue
            // by 1-60 days was missing: live it read $5,982.88 under an Action Required card
            // saying $6,582.88, the $600 gap being one invoice due a week earlier. Adding
            // bucket0_30 instead would be wrong in the other direction -- that bucket also
            // holds not-yet-due and null-due invoices by design. The hook now accumulates the
            // strictly past-due balance from the same invoices and the same balance maths, so
            // this line and Action Required answer the same question.
            const overdueSum = Number(safeData.overdueTotal) || 0;
            if (overdueSum > 0) {
              return (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-red-700 dark:text-red-400">Overdue Invoices</span>
                    <button 
                      onClick={() => navigate('/invoices?filter=overdue')}
                      className="text-xs text-red-700 hover:text-red-800 dark:text-red-400 font-medium"
                    >
                      Send All Reminders
                    </button>
                  </div>
                  <div className="text-lg font-bold text-red-700 dark:text-red-400">{fmtMoney(overdueSum)}</div>
                </div>
              );
            }
            return null;
          })()}
          
          <div className="grid grid-cols-2 gap-3">
            <AgingPill label="0–30 days" value={ar.bucket0_30} />
            <AgingPill label="31–60 days" value={ar.bucket31_60} />
            <AgingPill label="61–90 days" value={ar.bucket61_90} />
            <AgingPill label="> 90 days" value={ar.bucket90_plus} />
          </div>
          <div className="mt-4 text-right space-x-3">
            <button onClick={() => navigate('/invoices?filter=aging_0_30')} className="text-sm text-gray-600 hover:text-gray-900">View 0–30</button>
            <button onClick={() => navigate('/invoices?filter=aging_31_60')} className="text-sm text-gray-600 hover:text-gray-900">31–60</button>
            <button onClick={() => navigate('/invoices?filter=aging_61_90')} className="text-sm text-gray-600 hover:text-gray-900">61–90</button>
            <button onClick={() => navigate('/invoices?filter=aging_90_plus')} className="text-sm text-gray-600 hover:text-gray-900">90+</button>
          </div>
        </div>

        {/* Team Utilization */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Team Utilization</h3>
            <div className="text-sm text-gray-500">{loading ? '-' : `${safeData.utilizationPct}%`}</div>
          </div>
          <ProgressBar pct={safeData.utilizationPct} />
          <div className="mt-3 text-sm text-gray-600">
            {loading ? 'Loading…' : `${Number(safeData.scheduledHours || 0).toFixed(1)} hrs scheduled of ${Number(safeData.capacityHours || 0).toFixed(1)} hrs capacity`}
          </div>
          <div className="mt-4 text-right">
            {/* The card is about scheduled hours against capacity, so "View schedule" means
                the calendar — this went to the jobs list, which is the same data with the
                scheduling taken out of it. Other scheduling actions on this dashboard already
                route to /calendar. */}
            <button onClick={() => navigate('/calendar?techFilter=all')} className="text-sm text-gray-600 hover:text-gray-900">View schedule</button>
          </div>
        </div>

        {/* Inventory Alerts */}
        <InventoryAlertsWidget />
      </div>

      {/* Detailed team performance stays low on the page and only renders with data. */}
      {safeData.techScoreCards.length > 0 && (
        <div className="mb-8 grid grid-cols-1 gap-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Technician Scorecards</h3>
              <button onClick={() => navigate('/jobs?filter=completed')} className="text-sm text-gray-600 hover:text-gray-900">View</button>
            </div>
            <div className="table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2">{t('worker')}</th>
                    <th className="pb-2 text-center">Jobs</th>
                    <th className="pb-2 text-center">Revenue</th>
                    <th className="pb-2 text-center">Avg Ticket</th>
                    <th className="pb-2 text-center">Completion</th>
                    <th className="pb-2 text-center">Jobs/Day</th>
                  </tr>
                </thead>
                <tbody>
                  {safeData.techScoreCards.sort((a,b) => b.revenue - a.revenue).map((tech) => (
                    <tr key={tech.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 font-medium">
                        <button
                          onClick={() => navigate(`/calendar?view=resourceTimeGridDay&tech=${tech.id}`)}
                          className="dashboard-link hover:underline"
                        >
                          {tech.name}
                        </button>
                      </td>
                      <td className="py-2 text-center">{tech.completed}</td>
                      <td className="py-2 text-center text-green-600 font-medium">{fmtMoney(tech.revenue)}</td>
                      <td className="py-2 text-center">{fmtMoney(tech.avgTicket)}</td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${tech.completionRate == null ? 'bg-gray-100 text-gray-500' : tech.completionRate >= 90 ? 'bg-green-100 text-green-700' : tech.completionRate >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {tech.completionRate == null ? '—' : `${tech.completionRate}%`}
                        </span>
                      </td>
                      <td className="py-2 text-center">{tech.jobsPerDay}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity - Collapsible */}
      <div className="mb-6">
        <div className="card">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setRecentActivityExpanded(!recentActivityExpanded)}
          >
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <button
              className="p-1 hover:bg-gray-100 rounded"
              aria-label={recentActivityExpanded ? 'Collapse recent activity' : 'Expand recent activity'}
              aria-expanded={recentActivityExpanded}
            >
              {recentActivityExpanded ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
          </div>
          {recentActivityExpanded && (
            <div className="mt-4">
              {recentActivity.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      onClick={() => {
                        // Board #481. Both activity kinds were sent to /quotes?edit=<id>, and
                        // the Quotes list only shows quote-phase rows — so clicking "Work order
                        // scheduled — X" navigated somewhere the record was not, and opened
                        // nothing at all. Confirmed live on a scheduled FLOWTEST work order.
                        //
                        // /jobs/:id is the per-work-order VIEW, added so a job could be linked
                        // and looked at (App.js). It loads by id with no status filter, so it
                        // works for a row in any state — which is the point: quotes and jobs are
                        // one work_orders row told apart only by status (CLAUDE.md §8), and a
                        // link that picks a list by phase breaks the moment the row moves on.
                        if (activity.type === 'work_order' || activity.type === 'quote') {
                          navigate(`/jobs/${activity.workOrderId}`);
                        }
                        // Payments don't have a detail page yet
                      }}
                      className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg ${
                        (activity.type === 'work_order' || activity.type === 'quote') ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <activity.icon className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{activity.message}</p>
                        <p className="text-xs text-gray-500">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClockIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-sm text-gray-500">No recent activity</div>
                </div>
              )}
            </div>
          )}
          {!recentActivityExpanded && recentActivity.length > 0 && (
            <div className="mt-2 text-xs text-gray-500 pl-1">
              {recentActivity.length} items • Click to expand
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;
